import styled from 'styled-components';
import { BodyText, Button } from './ui';
import { FaTimes } from 'react-icons/fa';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${({ theme }) => theme.colors.overlayBackground};
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: ${({ theme }) => theme.colors.white};
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  max-width: 600px;
  max-height: 80vh;
  overflow-y: auto;
  position: relative;

  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }

  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none;
  scrollbar-width: none;

  @media (max-width: 768px) {
    padding: 20px;
    max-width: 90%;
  }
`;

const ModalHeader = styled.div`
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 2px solid ${({ theme }) => theme.colors.primaryBlue};
  position: relative;
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primaryBlue};
  margin: 0;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const CloseIconButton = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: ${({ theme }) => theme.colors.primaryBlue};
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.7;
  }

  @media (max-width: 768px) {
    font-size: 1.2rem;
    width: 25px;
    height: 25px;
  }
`;

const ModalBody = styled.div`
  margin-bottom: 20px;
  line-height: 1.6;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const CloseButton = styled(Button)`
  min-width: 100px;
`;

const TermsModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <Overlay onClick={handleOverlayClick}>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Terms & Conditions</ModalTitle>
          <CloseIconButton onClick={onClose} aria-label='Close'>
            <FaTimes />
          </CloseIconButton>
        </ModalHeader>
        <ModalBody>
          <BodyText style={{ marginBottom: '15px', fontWeight: 600 }}>
            Eligibility
          </BodyText>
          <BodyText style={{ marginBottom: '15px' }}>
            Wholesale purchasing is available only to approved business
            customers with a valid resale license or tax ID. We reserve the
            right to approve or deny wholesale accounts at our discretion based
            on compliance, business type, or other qualifying criteria.
          </BodyText>
          <BodyText style={{ marginBottom: '15px', fontWeight: 600 }}>
            Minimum Order Requirement
          </BodyText>
          <BodyText style={{ marginBottom: '15px' }}>
            The minimum wholesale order amount is USD $500.00. Orders that do
            not meet this minimum may be adjusted or cancelled. This requirement
            ensures that wholesale pricing and fulfillment processes remain
            consistent and efficient for all customers.
          </BodyText>
          <BodyText style={{ marginBottom: '15px', fontWeight: 600 }}>
            Pricing
          </BodyText>
          <BodyText style={{ marginBottom: '15px' }}>
            Wholesale pricing is provided only to approved wholesale customers
            and may be changed at any time without notice. All prices are listed
            in USD unless otherwise specified. Customers are responsible for
            reviewing pricing at the time of order placement.
          </BodyText>
          <BodyText style={{ marginBottom: '15px', fontWeight: 600 }}>
            Payment Terms
          </BodyText>
          <BodyText style={{ marginBottom: '15px' }}>
            Payment terms must be approved by the company. Full payment is
            required at the time of order unless alternative terms have been
            agreed upon in writing. We accept credit cards, ACH, wire payments,
            or other approved payment methods. Any unpaid or incomplete payments
            may cause delays in processing or result in cancellation of the
            order.
          </BodyText>
          <BodyText style={{ marginBottom: '15px', fontWeight: 600 }}>
            Returned Payments & Late Fees
          </BodyText>
          <BodyText style={{ marginBottom: '15px' }}>
            A $35 fee will be charged for each returned payment. Past-due
            accounts will incur a minimum 1.8% monthly late charge. If
            collection efforts become necessary, the buyer will be responsible
            for all attorney&apos;s fees associated with recovering the
            outstanding balance. In the event of a lawsuit, all costs, expenses,
            and attorney&apos;s fees incurred by the seller due to the
            buyer&apos;s breach shall be paid by the buyer. The buyer also
            expressly consents to the personal jurisdiction of the state or
            federal courts located in California for any legal matters relating
            to delivered goods.
          </BodyText>
          <BodyText style={{ marginBottom: '15px', fontWeight: 600 }}>
            Order Processing & Lead Times
          </BodyText>
          <BodyText style={{ marginBottom: '15px' }}>
            Order processing and lead times vary depending on inventory levels,
            order size, and product availability. Once processing has begun,
            orders cannot be modified or changed. Customers will be notified
            when possible if extended lead times or delays occur.
          </BodyText>
          <BodyText style={{ marginBottom: '15px', fontWeight: 600 }}>
            Shipping & Delivery
          </BodyText>
          <BodyText style={{ marginBottom: '15px' }}>
            Shipping fees are added to the final invoice once the order has been
            fully processed. All shipping charges are the responsibility of the
            buyer unless otherwise noted. We are not responsible for carrier
            delays, customs holds, weather conditions, or any other
            circumstances beyond our control. Risk of loss is transferred to the
            buyer once the shipment is handed over to the carrier.
          </BodyText>
          <BodyText style={{ marginBottom: '15px', fontWeight: 600 }}>
            Inspection, Claims & Returns
          </BodyText>
          <BodyText style={{ marginBottom: '15px' }}>
            Buyers must inspect all goods immediately upon delivery. All claims
            must be reported within 5 days of receipt and must include
            supporting details such as photos or descriptions of the issue.
            Wholesale orders are non-returnable and non-refundable except in
            cases where products are defective or shipped incorrectly. No
            returns are accepted without prior written authorization from Eden
            Marketing Corp. Unauthorized returns may be subject to a restocking
            fee, and all returned goods are subject to a processing and shipping
            charge of up to 20%. Contact to report issues to: cs@edencrop.com
          </BodyText>
          <BodyText style={{ marginBottom: '15px', fontWeight: 600 }}>
            Product Availability
          </BodyText>
          <BodyText style={{ marginBottom: '15px' }}>
            Inventory levels and product availability are subject to change at
            any time. Products may be discontinued or phased out without advance
            notice. Backordered items may be communicated and fulfilled based on
            availability and customer preference.
          </BodyText>
          <BodyText style={{ marginBottom: '15px', fontWeight: 600 }}>
            Resale & Distribution
          </BodyText>
          <BodyText style={{ marginBottom: '15px' }}>
            Products purchased at wholesale pricing may be resold through
            approved retail stores. However, repackaging, altering, or
            re-labeling products is strictly prohibited. Customers may not
            represent themselves as official distributors unless authorized by
            Eden Marketing Corp.
          </BodyText>
          <BodyText style={{ marginBottom: '15px', fontWeight: 600 }}>
            Intellectual Property
          </BodyText>
          <BodyText style={{ marginBottom: '15px' }}>
            All trademarks, product images, brand logos, marketing materials,
            and related content remain the exclusive property of the company.
            Any use of these materials requires prior written consent and may
            not be reproduced or altered without authorization.
          </BodyText>
          <BodyText style={{ marginBottom: '15px', fontWeight: 600 }}>
            Order Cancellation
          </BodyText>
          <BodyText style={{ marginBottom: '15px' }}>
            We reserve the right to cancel any order due to payment issues,
            inventory shortages, suspected fraud, or violations of these Terms &
            Conditions. Customers will be notified whenever possible if such a
            cancellation occurs.
          </BodyText>
          <BodyText style={{ marginBottom: '15px', fontWeight: 600 }}>
            Governing Law
          </BodyText>
          <BodyText style={{ marginBottom: '15px' }}>
            These Terms & Conditions are governed by the laws of the State of
            California. We may update or revise these Terms at any time, and
            continued use of a wholesale account constitutes acceptance of any
            updated Terms.
          </BodyText>
          <BodyText style={{ marginBottom: '15px', fontWeight: 600 }}>
            Contact
          </BodyText>
          <BodyText style={{ marginBottom: '15px' }}>
            For any questions regarding wholesale orders or these Terms &
            Conditions, please contact us at cs@edencorp.com.
          </BodyText>
        </ModalBody>
        <ModalFooter>
          <CloseButton variant='destructive' onClick={onClose}>
            Close
          </CloseButton>
        </ModalFooter>
      </ModalContent>
    </Overlay>
  );
};

export default TermsModal;
